import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Plus, Shield } from 'lucide-react';
import { useCommunityPosts } from '../hooks/useCommunityPosts';
import { COMMUNITY_CONFIG } from '../config/communityConfig';
import { uploadCommunityImage } from '../services/communityImageUploadService';
import { formatOfferAudienceHint, getActiveOffers } from '../services/offersService';
import {
  createCommunityPost,
  publishCommunityPost,
  updateCommunityPost,
} from '../services/communityPostsService';
import { formatDateShort } from '../utils/dates';

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const emptyDraft = {
  title: '',
  body: '',
  image_url: '',
  post_type: '',
  category: '',
  is_brand_related: false,
  brand_name: '',
  linked_offer_id: '',
  status: 'draft',
  publish_at: '',
  cta_text: '',
  cta_link: '',
  priority_order: 0,
};

const CommunityPostsAdminView = ({ user, onBack }) => {
  const role = user?.role || 'user';
  const canAccess = role === 'admin' || role === 'editor';
  const isAdmin = role === 'admin';

  const { posts, status, errorMessage, setPosts } = useCommunityPosts('manage');
  const [offers, setOffers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localError, setLocalError] = useState('');

  const selectedPost = useMemo(
    () => posts.find((p) => p.id === selectedId) || null,
    [posts, selectedId]
  );

  const beginNew = () => {
    setSelectedId(null);
    setDraft(emptyDraft);
    setImageFile(null);
    setLocalError('');
  };

  const beginEdit = (post) => {
    setSelectedId(post.id);
    setDraft({
      title: post.title || '',
      body: post.body || '',
      image_url: post.imageUrl || '',
      post_type: post.postType || '',
      category: post.category || '',
      is_brand_related: Boolean(post.isBrandRelated),
      brand_name: post.brandName || '',
      linked_offer_id: post.linkedOfferId || '',
      status: post.status || 'draft',
      publish_at: toDatetimeLocalValue(post.publishAt),
      cta_text: post.ctaText || '',
      cta_link: post.ctaLink || '',
      priority_order: post.priorityOrder ?? 0,
    });
    setImageFile(null);
    setLocalError('');
  };

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const rows = await getActiveOffers();
        setOffers(rows);
      } catch (_e) {
        setOffers([]);
      }
    };
    loadOffers();
  }, []);

  if (!canAccess) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Feed
        </button>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">You don’t have access to manage posts.</p>
        </div>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    setLocalError('');
    try {
      if (!draft.title.trim()) {
        setLocalError('Title is required.');
        return;
      }

      const payload = {
        ...draft,
        title: draft.title.trim(),
        status: isAdmin ? draft.status : 'draft',
      };

      if (!selectedId) {
        const created = await createCommunityPost(payload, user.id);
        setPosts((prev) => [created, ...prev]);
        setSelectedId(created.id);
      } else {
        const updated = await updateCommunityPost(selectedId, payload);
        setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      }
    } catch (e) {
      setLocalError(e?.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!isAdmin || !selectedId) return;
    setSaving(true);
    setLocalError('');
    try {
      const updated = await publishCommunityPost(selectedId, user.id);
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (e) {
      setLocalError(e?.message || 'Failed to publish post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Feed
        </button>
        <div className="p-2 bg-gray-100 rounded-lg shrink-0" title={role}>
          <Shield className="h-6 w-6 text-gray-700" />
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold mb-1">Manage community posts</h2>
        <p className="text-gray-600 text-sm">
          Editors create drafts. Admins review and publish.
        </p>
      </div>

      <button
        type="button"
        onClick={beginNew}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
      >
        <Plus className="h-4 w-4" />
        New draft
      </button>

      {status === 'loading' ? (
        <div className="text-sm text-gray-600">Loading posts…</div>
      ) : status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">Could not load posts.</p>
          {errorMessage ? <p className="text-xs text-red-700 mt-1">{errorMessage}</p> : null}
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => beginEdit(p)}
              className={`w-full text-left bg-white rounded-xl p-4 shadow-sm border ${
                selectedId === p.id ? 'border-green-300' : 'border-gray-100'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.title}</div>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {COMMUNITY_CONFIG.statusLabels[p.status] || p.status}
                    </span>
                    {p.postType ? (
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        {COMMUNITY_CONFIG.postTypeLabels[p.postType] || p.postType}
                      </span>
                    ) : null}
                    {p.publishAt ? <span>Publish {formatDateShort(p.publishAt)}</span> : null}
                  </div>
                </div>
                <div className="text-xs text-gray-400 shrink-0">
                  Updated {formatDateShort(p.updatedAt || p.createdAt)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <h3 className="font-semibold mb-3">{selectedPost ? 'Edit post' : 'Create post'}</h3>

        {localError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3">
            <p className="text-sm text-red-800">{localError}</p>
          </div>
        ) : null}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Post title"
              maxLength={140}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
            <textarea
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y min-h-[6rem]"
              placeholder="Write the post…"
              maxLength={5000}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image (optional)</label>

            {draft.image_url ? (
              <div className="mb-2">
                <img
                  src={draft.image_url}
                  alt=""
                  className="w-full h-40 rounded-lg object-cover bg-gray-100"
                  loading="lazy"
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                disabled={!imageFile || uploadingImage}
                onClick={async () => {
                  if (!imageFile) return;
                  setUploadingImage(true);
                  setLocalError('');
                  try {
                    const { publicUrl } = await uploadCommunityImage({ file: imageFile, userId: user?.id });
                    setDraft((d) => ({ ...d, image_url: publicUrl }));
                    setImageFile(null);
                  } catch (e) {
                    setLocalError(e?.message || 'Image upload failed');
                  } finally {
                    setUploadingImage(false);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {uploadingImage ? 'Uploading…' : 'Upload image'}
              </button>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Or paste image URL</label>
                <input
                  value={draft.image_url}
                  onChange={(e) => setDraft((d) => ({ ...d, image_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://…"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Post type</label>
              <input
                value={draft.post_type}
                onChange={(e) => setDraft((d) => ({ ...d, post_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="challenge / education…"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                value={draft.category}
                onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="campus / cafe…"
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Boolean(draft.is_brand_related)}
                onChange={(e) => {
                  const on = e.target.checked;
                  setDraft((d) => ({
                    ...d,
                    is_brand_related: on,
                    brand_name: on ? d.brand_name : '',
                    linked_offer_id: on ? d.linked_offer_id : '',
                  }));
                }}
                className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span>Brand-related post</span>
            </label>

            {draft.is_brand_related ? (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand name</label>
                  <input
                    value={draft.brand_name}
                    onChange={(e) => setDraft((d) => ({ ...d, brand_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    placeholder="e.g. MacShop"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Linked offer (optional)</label>
                  <select
                    value={draft.linked_offer_id}
                    onChange={(e) => setDraft((d) => ({ ...d, linked_offer_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                  >
                    <option value="">None</option>
                    {offers.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.brandName}: {o.offerTitle}
                        {o.campaignLabel ? ` — ${o.campaignLabel}` : ''} {formatOfferAudienceHint(o)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                type="number"
                value={draft.priority_order}
                onChange={(e) => setDraft((d) => ({ ...d, priority_order: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publish at (optional)</label>
              <input
                type="datetime-local"
                value={draft.publish_at}
                onChange={(e) => setDraft((d) => ({ ...d, publish_at: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA text</label>
              <input
                value={draft.cta_text}
                onChange={(e) => setDraft((d) => ({ ...d, cta_text: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder={COMMUNITY_CONFIG.defaultCtaText}
                maxLength={60}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA link</label>
              <input
                value={draft.cta_link}
                onChange={(e) => setDraft((d) => ({ ...d, cta_link: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="https://…"
              />
            </div>
          </div>

          {isAdmin ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={draft.status}
                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              Status: <span className="font-medium">Draft</span> (only admins can publish)
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {selectedId ? 'Save changes' : 'Create draft'}
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={!isAdmin || !selectedId || saving}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              title={!isAdmin ? 'Admins only' : undefined}
            >
              Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPostsAdminView;

